import type { MutableRefObject } from "react";
import { useCallback, useRef } from "react";

import type * as monacoEditor from "monaco-editor/esm/vs/editor/editor.api";

import type { TranslationMatch } from "@lib/findTranslationMatches/findTranslationMatches";
import { findTranslationMatches } from "@lib/findTranslationMatches/findTranslationMatches";

import type { Monaco } from "@components/MonacoHtmlEditor/config/monacoLoader";

import type { DecorationFn } from "./types";

const translationHighlightClass = "translation-highlight";

// Function to find all matches of your pattern
const findMatches = (
  content: string,
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  monaco: Monaco
): TranslationMatch[] => {
  const model = editor.getModel();
  if (!model) {
    return [];
  }

  return findTranslationMatches(content, (match) => {
    const startPosition = model.getPositionAt(match.index);
    const endPosition = model.getPositionAt(match.index + match[0].length);

    return {
      range: new monaco.Range(
        startPosition.lineNumber,
        startPosition.column,
        endPosition.lineNumber,
        endPosition.column
      ),
      text: match[0],
    };
  });
};

// Apply decorations with blue background
const applyDecorations = (
  decorations: MutableRefObject<string[]>,
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  monaco: Monaco
): void => {
  const model = editor.getModel();
  if (!model) {
    return;
  }

  const content = model.getValue();
  const matches = findMatches(content, editor, monaco);

  if (matches.length === 0) {
    // Clear all decorations if no matches
    decorations.current = editor.deltaDecorations(decorations.current, []);
    return;
  }

  // Create decorations for each match
  const allDecorations = matches.flatMap((match) => {
    // Basic highlight for the whole match
    const basicHighlight: monacoEditor.editor.IModelDeltaDecoration = {
      range: match.range,
      options: {
        inlineClassName: translationHighlightClass,
        stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
      },
    };

    // Create a range for just the end of the match (last character)
    const endRange = new monaco.Range(
      match.range.endLineNumber,
      match.range.endColumn - 1,
      match.range.endLineNumber,
      match.range.endColumn
    );

    const endHighlight: monacoEditor.editor.IModelDeltaDecoration = {
      range: endRange,
      options: {
        inlineClassName: `${translationHighlightClass}-end`,
        stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
      },
    };

    return [basicHighlight, endHighlight];
  });

  // Apply all decorations
  decorations.current = editor.deltaDecorations(decorations.current, allDecorations);
};

/**
 * Custom hook to decorate translation tags in Monaco editor
 *
 * @returns A function that applies the decorations to an editor and keeps them current as its content changes
 */
export const useDecorateTranslationTags = (): DecorationFn => {
  // Reference to store the Monaco decorations
  const decorations = useRef<string[]>([]);

  return useCallback(
    ({ editor, monaco }: { editor: monacoEditor.editor.IStandaloneCodeEditor; monaco: Monaco }) => {
      const model = editor.getModel();

      if (!model) {
        return;
      }

      // Apply decorations on initial render
      applyDecorations(decorations, editor, monaco);

      // Apply decorations on content change
      model.onDidChangeContent(() => {
        applyDecorations(decorations, editor, monaco);
      });
    },
    []
  );
};
